import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import './Analytics.css';

const StaffPerformance = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month'); // week, month, year
  const [staffType, setStaffType] = useState('all'); // all, waiter, chef

  const [staffMetrics, setStaffMetrics] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [performanceTrends, setPerformanceTrends] = useState([]);
  const [efficiencyMetrics, setEfficiencyMetrics] = useState([]);

  const [summaryStats, setSummaryStats] = useState({
    totalStaff: 0,
    avgOrdersPerStaff: 0,
    avgServiceTime: 0,
    avgSatisfactionScore: 0,
    topPerformerName: '',
    improvementNeeded: 0
  });

  useEffect(() => {
    fetchStaffPerformance();
  }, [timeRange, staffType]);

  const fetchStaffPerformance = async () => {
    try {
      setLoading(true);

      // Calculate date range
      const now = new Date();
      const startDate = new Date();
      if (timeRange === 'week') {
        startDate.setDate(now.getDate() - 7);
      } else if (timeRange === 'month') {
        startDate.setMonth(now.getMonth() - 1);
      } else {
        startDate.setFullYear(now.getFullYear() - 1);
      }

      // Fetch staff members
      let staffQuery = query(collection(db, 'admin_users'));
      if (staffType !== 'all') {
        staffQuery = query(collection(db, 'admin_users'), where('role', '==', staffType));
      }
      const staffSnapshot = await getDocs(staffQuery);
      const staffMembers = staffSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch orders in time range
      const ordersQuery = query(
        collection(db, 'orders'),
        where('createdAt', '>=', startDate),
        orderBy('createdAt', 'desc')
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      const orders = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch reviews
      const reviewsQuery = query(
        collection(db, 'reviews'),
        where('createdAt', '>=', startDate),
        orderBy('createdAt', 'desc')
      );
      const reviewsSnapshot = await getDocs(reviewsQuery);
      const reviews = reviewsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate metrics for each staff member
      const metrics = staffMembers.map(staff => {
        const staffOrders = orders.filter(o => o.assignedTo === staff.id || o.preparedBy === staff.id);
        const staffReviews = reviews.filter(r => r.staffId === staff.id);

        const totalOrders = staffOrders.length;
        const totalRevenue = staffOrders.reduce((sum, o) => sum + (o.total || 0), 0);
        const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

        // Service time calculation (time from order to ready)
        const serviceTimes = staffOrders
          .filter(o => o.readyAt && o.createdAt)
          .map(o => {
            const created = o.createdAt?.toDate?.() || new Date(o.createdAt);
            const ready = o.readyAt?.toDate?.() || new Date(o.readyAt);
            return (ready - created) / (1000 * 60); // minutes
          });
        const avgServiceTime = serviceTimes.length > 0
          ? serviceTimes.reduce((sum, t) => sum + t, 0) / serviceTimes.length
          : 0;

        // Customer satisfaction
        const avgRating = staffReviews.length > 0
          ? staffReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / staffReviews.length
          : 0;

        // Efficiency score (composite metric)
        const efficiencyScore = calculateEfficiencyScore({
          ordersHandled: totalOrders,
          avgServiceTime,
          avgRating,
          avgOrderValue
        });

        return {
          id: staff.id,
          name: staff.name || staff.email,
          role: staff.role || 'staff',
          ordersHandled: totalOrders,
          totalRevenue,
          avgOrderValue,
          avgServiceTime: Math.round(avgServiceTime),
          avgRating: avgRating.toFixed(1),
          reviewCount: staffReviews.length,
          efficiencyScore
        };
      });

      // Sort by efficiency score
      const sortedMetrics = metrics.sort((a, b) => b.efficiencyScore - a.efficiencyScore);
      setStaffMetrics(sortedMetrics);

      // Top performers (top 5)
      const top5 = sortedMetrics.slice(0, 5);
      setTopPerformers(top5);

      // Performance trends (daily aggregation)
      const trends = calculatePerformanceTrends(orders, staffMembers);
      setPerformanceTrends(trends);

      // Efficiency metrics for radar chart
      const efficiency = calculateEfficiencyMetrics(sortedMetrics);
      setEfficiencyMetrics(efficiency);

      // Summary statistics
      const avgOrders = sortedMetrics.length > 0
        ? sortedMetrics.reduce((sum, s) => sum + s.ordersHandled, 0) / sortedMetrics.length
        : 0;
      const avgService = sortedMetrics.length > 0
        ? sortedMetrics.reduce((sum, s) => sum + s.avgServiceTime, 0) / sortedMetrics.length
        : 0;
      const avgSat = sortedMetrics.length > 0
        ? sortedMetrics.reduce((sum, s) => sum + parseFloat(s.avgRating), 0) / sortedMetrics.length
        : 0;
      const needsImprovement = sortedMetrics.filter(s => s.efficiencyScore < 60).length;

      setSummaryStats({
        totalStaff: sortedMetrics.length,
        avgOrdersPerStaff: Math.round(avgOrders),
        avgServiceTime: Math.round(avgService),
        avgSatisfactionScore: avgSat.toFixed(1),
        topPerformerName: sortedMetrics[0]?.name || 'N/A',
        improvementNeeded: needsImprovement
      });

      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching staff performance:', error);
      setLoading(false);
    }
  };

  const calculateEfficiencyScore = (metrics) => {
    // Weighted scoring: orders (30%), service time (25%), rating (30%), order value (15%)
    const ordersScore = Math.min(100, (metrics.ordersHandled / 50) * 100) * 0.3;
    const timeScore = Math.max(0, 100 - (metrics.avgServiceTime / 30) * 100) * 0.25;
    const ratingScore = (metrics.avgRating / 5) * 100 * 0.3;
    const valueScore = Math.min(100, (metrics.avgOrderValue / 500) * 100) * 0.15;

    return Math.round(ordersScore + timeScore + ratingScore + valueScore);
  };

  const calculatePerformanceTrends = (orders, staff) => {
    const dailyData = {};

    orders.forEach(order => {
      const date = order.createdAt?.toDate?.() || new Date(order.createdAt);
      const dateKey = date.toISOString().split('T')[0];

      if (!dailyData[dateKey]) {
        dailyData[dateKey] = {
          date: dateKey,
          totalOrders: 0,
          totalRevenue: 0,
          avgServiceTime: [],
          staffActive: new Set()
        };
      }

      dailyData[dateKey].totalOrders++;
      dailyData[dateKey].totalRevenue += order.total || 0;

      if (order.assignedTo) dailyData[dateKey].staffActive.add(order.assignedTo);
      if (order.preparedBy) dailyData[dateKey].staffActive.add(order.preparedBy);

      if (order.readyAt && order.createdAt) {
        const created = order.createdAt?.toDate?.() || new Date(order.createdAt);
        const ready = order.readyAt?.toDate?.() || new Date(order.readyAt);
        const serviceTime = (ready - created) / (1000 * 60);
        dailyData[dateKey].avgServiceTime.push(serviceTime);
      }
    });

    return Object.values(dailyData)
      .map(day => ({
        date: new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        orders: day.totalOrders,
        revenue: Math.round(day.totalRevenue),
        avgServiceTime: day.avgServiceTime.length > 0
          ? Math.round(day.avgServiceTime.reduce((sum, t) => sum + t, 0) / day.avgServiceTime.length)
          : 0,
        activeStaff: day.staffActive.size
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-14); // Last 14 days
  };

  const calculateEfficiencyMetrics = (metrics) => {
    if (metrics.length === 0) return [];

    const top3 = metrics.slice(0, 3);
    return top3.map(staff => ({
      staff: staff.name.split(' ')[0], // First name only
      'Orders': Math.min(100, (staff.ordersHandled / 50) * 100),
      'Speed': Math.max(0, 100 - (staff.avgServiceTime / 30) * 100),
      'Rating': (parseFloat(staff.avgRating) / 5) * 100,
      'Revenue': Math.min(100, (staff.avgOrderValue / 500) * 100)
    }));
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="analytics-loading">
          <div className="spinner"></div>
          <p>Loading staff performance metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h2>👥 Staff Performance Analytics</h2>
        <div className="analytics-controls">
          <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last Year</option>
          </select>
          <select value={staffType} onChange={(e) => setStaffType(e.target.value)}>
            <option value="all">All Staff</option>
            <option value="waiter">Waiters Only</option>
            <option value="chef">Chefs Only</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="analytics-metrics-grid">
        <div className="metric-card">
          <div className="metric-icon">👤</div>
          <div className="metric-content">
            <div className="metric-label">Total Staff</div>
            <div className="metric-value">{summaryStats.totalStaff}</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon">📦</div>
          <div className="metric-content">
            <div className="metric-label">Avg Orders/Staff</div>
            <div className="metric-value">{summaryStats.avgOrdersPerStaff}</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon">⏱️</div>
          <div className="metric-content">
            <div className="metric-label">Avg Service Time</div>
            <div className="metric-value">{summaryStats.avgServiceTime}m</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon">⭐</div>
          <div className="metric-content">
            <div className="metric-label">Avg Satisfaction</div>
            <div className="metric-value">{summaryStats.avgSatisfactionScore}/5</div>
          </div>
        </div>
        <div className="metric-card highlight">
          <div className="metric-icon">🏆</div>
          <div className="metric-content">
            <div className="metric-label">Top Performer</div>
            <div className="metric-value">{summaryStats.topPerformerName}</div>
          </div>
        </div>
        <div className="metric-card warning">
          <div className="metric-icon">⚠️</div>
          <div className="metric-content">
            <div className="metric-label">Need Training</div>
            <div className="metric-value">{summaryStats.improvementNeeded}</div>
          </div>
        </div>
      </div>

      {/* Staff Performance Table */}
      <div className="analytics-chart-card">
        <h3>Staff Performance Leaderboard</h3>
        <div className="staff-table">
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Name</th>
                <th>Role</th>
                <th>Orders</th>
                <th>Revenue</th>
                <th>Avg Order</th>
                <th>Service Time</th>
                <th>Rating</th>
                <th>Reviews</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {staffMetrics.map((staff, index) => (
                <tr key={staff.id} className={index < 3 ? 'top-performer' : ''}>
                  <td>
                    {index === 0 && '🥇'}
                    {index === 1 && '🥈'}
                    {index === 2 && '🥉'}
                    {index > 2 && index + 1}
                  </td>
                  <td>{staff.name}</td>
                  <td><span className={`role-badge ${staff.role}`}>{staff.role}</span></td>
                  <td>{staff.ordersHandled}</td>
                  <td>₹{staff.totalRevenue.toLocaleString()}</td>
                  <td>₹{Math.round(staff.avgOrderValue)}</td>
                  <td>{staff.avgServiceTime}m</td>
                  <td>{staff.avgRating} ⭐</td>
                  <td>{staff.reviewCount}</td>
                  <td>
                    <div className="efficiency-badge" style={{
                      backgroundColor: staff.efficiencyScore >= 80 ? '#10b981' : staff.efficiencyScore >= 60 ? '#f59e0b' : '#ef4444'
                    }}>
                      {staff.efficiencyScore}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Performers Chart */}
      <div className="analytics-chart-card">
        <h3>Top 5 Performers by Orders</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={topPerformers}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="ordersHandled" fill="#3b82f6" name="Orders" />
            <Bar dataKey="reviewCount" fill="#10b981" name="Reviews" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Performance Trends */}
      <div className="analytics-chart-card">
        <h3>Performance Trends (Last 14 Days)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={performanceTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="orders" stroke="#3b82f6" name="Orders" strokeWidth={2} />
            <Line yAxisId="left" type="monotone" dataKey="activeStaff" stroke="#10b981" name="Active Staff" strokeWidth={2} />
            <Line yAxisId="right" type="monotone" dataKey="avgServiceTime" stroke="#f59e0b" name="Avg Service Time (m)" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Efficiency Radar Chart */}
      {efficiencyMetrics.length > 0 && (
        <div className="analytics-chart-card">
          <h3>Top 3 Staff Efficiency Comparison</h3>
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={efficiencyMetrics}>
              <PolarGrid />
              <PolarAngleAxis dataKey="staff" />
              <PolarRadiusAxis angle={90} domain={[0, 100]} />
              <Radar name="Orders" dataKey="Orders" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
              <Radar name="Speed" dataKey="Speed" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
              <Radar name="Rating" dataKey="Rating" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} />
              <Radar name="Revenue" dataKey="Revenue" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.6} />
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Insights */}
      <div className="analytics-insights">
        <h3>📊 Performance Insights</h3>
        <div className="insight-cards">
          {summaryStats.topPerformerName !== 'N/A' && (
            <div className="insight-card success">
              <span className="insight-icon">🏆</span>
              <p><strong>{summaryStats.topPerformerName}</strong> is your top performer! Consider using them as a mentor for training.</p>
            </div>
          )}
          {summaryStats.avgServiceTime > 25 && (
            <div className="insight-card warning">
              <span className="insight-icon">⚠️</span>
              <p>Average service time is <strong>{summaryStats.avgServiceTime} minutes</strong>. Consider process optimization or additional staff during peak hours.</p>
            </div>
          )}
          {summaryStats.improvementNeeded > 0 && (
            <div className="insight-card info">
              <span className="insight-icon">📚</span>
              <p><strong>{summaryStats.improvementNeeded} staff members</strong> have efficiency scores below 60. Schedule training sessions to improve performance.</p>
            </div>
          )}
          {summaryStats.avgSatisfactionScore >= 4.5 && (
            <div className="insight-card success">
              <span className="insight-icon">⭐</span>
              <p>Excellent customer satisfaction at <strong>{summaryStats.avgSatisfactionScore}/5</strong>! Your team is doing great!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffPerformance;
